import { mix, rgba, type RGB } from './color';
import { FIELD_TILT, REST_RADIUS } from './particleField';

const TAU = Math.PI * 2;
const WHITE: RGB = [255, 255, 255];

/** Timeline of the boot sequence, in seconds */
export const BOOT = {
  wordIn: 0.55,
  morphStart: 0.8,
  morphSpread: 0.5,
  morphLen: 1.2,
  /** Orb complete: the ring goes out, "anna · klaar voor je instructie" appears */
  settled: 2.6,
  /** The overlay hands over to the real Anna underneath */
  done: 4.1,
};

export interface BootParticle {
  /** Start position in the word (px) */
  sx: number;
  sy: number;
  /** Target on the unit sphere */
  ux: number;
  uy: number;
  uz: number;
  delay: number;
  curl: number;
  size: number;
  alpha: number;
  rim: boolean;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

/** How far the morph has progressed overall (0 → 1), for spin and glow. */
export const settleAt = (t: number) => clamp01((t - (BOOT.morphStart + BOOT.morphLen)) / 0.9);

/** Sample the pixels of a word rendered in the display face. */
export function sampleWord(text: string, w: number, h: number, fontPx: number, cx: number, cy: number): { x: number; y: number }[] {
  const c = document.createElement('canvas');
  c.width = Math.ceil(w);
  c.height = Math.ceil(h);
  const ctx = c.getContext('2d');
  if (!ctx) return [];
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `200 ${fontPx}px Outfit, Inter, sans-serif`;
  ctx.fillText(text, cx, cy);
  const data = ctx.getImageData(0, 0, c.width, c.height).data;
  const step = Math.max(3, Math.round(fontPx / 70));
  const pts: { x: number; y: number }[] = [];
  for (let y = 0; y < c.height; y += step) {
    for (let x = 0; x < c.width; x += step) {
      if (data[(y * c.width + x) * 4 + 3] > 120) pts.push({ x: x + (Math.random() - 0.5) * step, y: y + (Math.random() - 0.5) * step });
    }
  }
  return pts;
}

export function buildBootParticles(points: { x: number; y: number }[], max = 3200): BootParticle[] {
  const pick = points.length > max ? points.filter(() => Math.random() < max / points.length) : points;
  if (!pick.length) return [];
  const minX = Math.min(...pick.map((p) => p.x));
  const maxX = Math.max(...pick.map((p) => p.x));
  const n = pick.length;
  const golden = Math.PI * (3 - Math.sqrt(5));
  // Same distribution as the living field: dense towards the core, a sparse bright rim.
  const targets = Array.from({ length: n }, (_, i) => {
    const y = 1 - (i / Math.max(1, n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const th = golden * i;
    const rim = Math.random() < 0.07;
    const depthR = rim ? 0.9 + Math.random() * 0.1 : 0.05 + Math.pow(Math.random(), 1.6) * 0.85;
    return { x: Math.cos(th) * r * depthR, y: y * depthR, z: Math.sin(th) * r * depthR, rim };
  }).sort(() => Math.random() - 0.5);

  return pick.map((p, i) => {
    const t = targets[i];
    return {
      sx: p.x,
      sy: p.y,
      ux: t.x,
      uy: t.y,
      uz: t.z,
      delay: ((p.x - minX) / Math.max(1, maxX - minX)) * BOOT.morphSpread + Math.random() * 0.12,
      curl: (Math.random() - 0.5) * 2,
      size: t.rim ? 1.1 + Math.random() * 1.1 : 0.4 + Math.random() * 0.9,
      alpha: t.rim ? 0.85 : 0.4 + Math.random() * 0.45,
      rim: t.rim,
    };
  });
}

export interface BootFrame {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  t: number;
  /** Accumulated rotation; slows to Anna's resting spin so the hand-over lines up */
  angle: number;
  /** Where the real Anna sits on the page — the orb forms exactly there, at her size */
  cx: number;
  cy: number;
  R: number;
  accent: RGB;
  particles: BootParticle[];
}

/**
 * Word → orb. Particles leave the letters left to right, curl inward and settle into the sphere
 * at the real avatar's position and radius, rotating and tilted like her.
 */
export function drawBoot(f: BootFrame) {
  const { ctx, t, cx, cy, accent } = f;
  ctx.clearRect(0, 0, f.w, f.h);

  const wordA = clamp01(t / BOOT.wordIn);
  const settle = settleAt(t);
  // Anna's resting breath: ±5 % on a ~5 s period, eased in as the orb completes.
  const breath = 1 + 0.05 * Math.sin((t / 5) * TAU) * settle;
  const R = f.R * breath;
  const cs = Math.cos(f.angle);
  const sn = Math.sin(f.angle);
  const ct = Math.cos(FIELD_TILT);
  const st = Math.sin(FIELD_TILT);
  const pc = mix(accent, WHITE, 0.35);
  const rimC = mix(accent, WHITE, 0.55);

  // Soft luminous core, no hard disk — the same light the living field carries.
  const glow = clamp01((t - BOOT.morphStart - 0.6) / 1.0);
  if (glow > 0) {
    const coreR = R * 0.15 * 3.6;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
    g.addColorStop(0, rgba(mix(accent, WHITE, 0.6), 0.6 * glow));
    g.addColorStop(0.12, rgba(mix(accent, WHITE, 0.5), 0.44 * glow));
    g.addColorStop(0.32, rgba(mix(accent, WHITE, 0.2), 0.2 * glow));
    g.addColorStop(0.62, rgba(accent, 0.07 * glow));
    g.addColorStop(1, rgba(accent, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, coreR, 0, TAU);
    ctx.fill();
  }

  ctx.globalCompositeOperation = 'lighter';
  for (const q of f.particles) {
    const x1 = q.ux * cs + q.uz * sn;
    const z1 = -q.ux * sn + q.uz * cs;
    const y2 = q.uy * ct - z1 * st;
    const z2 = q.uy * st + z1 * ct;
    const persp = 1 + z2 * 0.16;
    const ex = cx + x1 * R * persp;
    const ey = cy + y2 * R * persp;
    const d = (z2 + 1) / 2;

    const k = easeInOut(clamp01((t - BOOT.morphStart - q.delay) / BOOT.morphLen));
    // Curved path: the control point swings out so every particle curls into the orb.
    const mx = (q.sx + ex) / 2;
    const my = (q.sy + ey) / 2;
    const dx = ex - q.sx;
    const dy = ey - q.sy;
    const cxp = mx - dy * 0.42 * q.curl;
    const cyp = my + dx * 0.42 * q.curl - Math.abs(dx) * 0.12;
    const u = 1 - k;
    const x = u * u * q.sx + 2 * u * k * cxp + k * k * ex;
    const y = u * u * q.sy + 2 * u * k * cyp + k * k * ey;

    const shimmer = 0.85 + 0.15 * Math.sin(t * 9 + q.curl * 20);
    const wordAlpha = wordA * q.alpha * shimmer;
    // Depth falls off steeply, like the living field: far side haze, near side crisp.
    const radial = q.rim ? 1.5 : 0.35 + 0.65 * Math.hypot(q.ux, q.uy, q.uz);
    const orbAlpha = q.alpha * (0.08 + 0.92 * d * d) * radial;
    const a = Math.min(1, wordAlpha * (1 - k) + orbAlpha * k);
    // Same dot scale as the living field (its canvas half-size is R / REST_RADIUS).
    const s = (1 - k) * 1.3 + k * q.size * (R / REST_RADIUS / 160) * (0.45 + 0.7 * d);
    ctx.fillStyle = rgba(q.rim && k > 0.5 ? rimC : pc, a);
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.4, s), 0, TAU);
    ctx.fill();
  }

  // One ring goes out when Anna is complete.
  const ringT = (t - BOOT.settled + 0.2) / 0.9;
  if (ringT > 0 && ringT < 1) {
    ctx.strokeStyle = rgba(mix(accent, WHITE, 0.3), 0.5 * Math.pow(1 - ringT, 2));
    ctx.lineWidth = 2 * (1 - ringT) + 0.5;
    ctx.beginPath();
    ctx.arc(cx, cy, R * (1 + ringT * 0.8), 0, TAU);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
}
